import { useState, useMemo, useCallback } from 'react';
import { Alert, Chip, PageHeader, Spinner, Text } from '@billda/design-system';
import { usePlatformFeatures } from '../hooks/usePlatformFeatures';
import { PlatformFeatureDto } from '../api/platformApi';
import {
  useSetGlobalOverride,
  useRemoveGlobalOverride,
} from '../hooks/useFeatureOverrideMutations';
import FeatureToggleConfirmDialog, {
  FeatureToggleAction,
  OverrideState,
} from '../components/FeatureToggleConfirmDialog';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { ListViewShell } from '../../../shared/components/ui/ListViewShell';

type OverrideFilter = 'all' | 'overridden' | 'default';
type CategoryFilter =
  | 'all'
  | 'Development'
  | 'Operational'
  | 'Rollout'
  | 'Research'
  | 'PayGated';

const OVERRIDE_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Overridden', value: 'overridden' },
  { label: 'No Override', value: 'default' },
] as const;

const CATEGORY_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Development', value: 'Development' },
  { label: 'Operational', value: 'Operational' },
  { label: 'Rollout', value: 'Rollout' },
  { label: 'Research', value: 'Research' },
  { label: 'Pay Gated', value: 'PayGated' },
] as const;

const CATEGORY_COLORS: Record<
  string,
  'info' | 'warning' | 'default' | 'secondary' | 'primary'
> = {
  Development: 'info',
  Operational: 'warning',
  Rollout: 'default',
  Research: 'secondary',
  PayGated: 'primary',
};

function getOverrideChip(globalOverride: PlatformFeatureDto['globalOverride']) {
  switch (globalOverride) {
    case 'enabled':
      return { label: 'Enabled', color: 'success' as const };
    case 'disabled':
      return { label: 'Disabled', color: 'error' as const };
    default:
      return { label: 'Default (off)', color: 'default' as const };
  }
}

function toOverrideState(
  globalOverride: PlatformFeatureDto['globalOverride']
): OverrideState {
  return globalOverride ?? 'default';
}

function getGlobalAction(feature: PlatformFeatureDto): FeatureToggleAction {
  const currentState = toOverrideState(feature.globalOverride);
  if (feature.globalOverride !== null) {
    return { type: 'remove', scope: 'global', currentState };
  }
  return { type: 'set', scope: 'global', currentState };
}

const PlatformFeatureSwitchesPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [overrideFilter, setOverrideFilter] = useState<OverrideFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [selectedFeature, setSelectedFeature] =
    useState<PlatformFeatureDto | null>(null);

  const {
    data: features,
    isLoading,
    error,
  } = usePlatformFeatures(appliedSearch || undefined);

  const setGlobalOverride = useSetGlobalOverride();
  const removeGlobalOverride = useRemoveGlobalOverride();

  const isMutating =
    setGlobalOverride.isPending || removeGlobalOverride.isPending;

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setAppliedSearch(value);
  };

  const filteredFeatures = useMemo(() => {
    let items = features ?? [];
    if (overrideFilter === 'overridden')
      items = items.filter(f => f.globalOverride !== null);
    else if (overrideFilter === 'default')
      items = items.filter(f => f.globalOverride === null);
    if (categoryFilter !== 'all')
      items = items.filter(f => f.category === categoryFilter);
    return items;
  }, [features, overrideFilter, categoryFilter]);

  const handleRowClick = useCallback((feature: PlatformFeatureDto) => {
    setSelectedFeature(feature);
  }, []);

  const handleConfirm = useCallback(
    (isEnabled: boolean) => {
      if (!selectedFeature) return;

      const action = getGlobalAction(selectedFeature);
      const onSuccess = () => setSelectedFeature(null);

      if (action.type === 'remove') {
        removeGlobalOverride.mutate(
          { key: selectedFeature.key },
          { onSuccess }
        );
      } else {
        setGlobalOverride.mutate(
          { key: selectedFeature.key, isEnabled },
          { onSuccess }
        );
      }
    },
    [selectedFeature, setGlobalOverride, removeGlobalOverride]
  );

  const handleCancel = useCallback(() => {
    setSelectedFeature(null);
  }, []);

  if (isLoading && !features) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="mb-4">
        {error.message}
      </Alert>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-auto">
        <div>
          <PageHeader title="Feature Switches" />

          <PageToolbar
            tabs={
              <div className="flex flex-col gap-2">
                <ChipTabs
                  tabs={[...CATEGORY_TABS]}
                  value={categoryFilter}
                  onChange={v => setCategoryFilter(v as CategoryFilter)}
                  data-testid="category-filter-tabs"
                />
                <ChipTabs
                  tabs={[...OVERRIDE_TABS]}
                  value={overrideFilter}
                  onChange={v => setOverrideFilter(v as OverrideFilter)}
                  data-testid="override-filter-tabs"
                />
              </div>
            }
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
          />

          <ListViewShell
            itemCount={filteredFeatures.length}
            loading={isLoading}
            emptyMessage="No feature switches found"
            data-testid="platform-features-list"
            columnHeaders={
              <>
                <Text variant="caption" color="secondary" className="flex-1">
                  Feature
                </Text>
                <Text
                  variant="caption"
                  color="secondary"
                  className="hidden w-[200px] text-right sm:block"
                >
                  Global State
                </Text>
              </>
            }
          >
            {filteredFeatures.map(feature => {
              const chipProps = getOverrideChip(feature.globalOverride);
              const categoryColor =
                CATEGORY_COLORS[feature.category] ?? 'default';
              return (
                <div
                  key={feature.key}
                  data-testid="platform-feature-row"
                  onClick={() => handleRowClick(feature)}
                  className="flex cursor-pointer items-center gap-3 border-b border-border-default px-4 py-3 transition-colors hover:bg-surface-raised sm:px-6"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Text
                        as="span"
                        variant="body2"
                        noWrap
                        className="font-semibold"
                      >
                        {feature.key}
                      </Text>
                      <Chip
                        label={feature.category}
                        color={categoryColor}
                        size="sm"
                        variant="outlined"
                        className="h-5 text-[0.65rem]"
                      />
                    </div>
                    <Text
                      as="span"
                      variant="caption"
                      color="secondary"
                      noWrap
                      className="block"
                    >
                      {feature.description}
                    </Text>
                  </div>

                  <div className="flex w-[120px] justify-end">
                    <Chip
                      label={chipProps.label}
                      color={chipProps.color}
                      size="sm"
                      className="text-[0.7rem]"
                    />
                  </div>
                </div>
              );
            })}
          </ListViewShell>
        </div>
      </div>

      {selectedFeature && (
        <FeatureToggleConfirmDialog
          open={!!selectedFeature}
          featureKey={selectedFeature.key}
          featureDescription={selectedFeature.description}
          action={getGlobalAction(selectedFeature)}
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};

export default PlatformFeatureSwitchesPage;
